import { useMemo } from "react";

import { useSelector } from "react-redux";

import type { CrmColumnItem } from "app/pages/sales-intelligence/sub-modules/management/types";

import crmEnrichmentFiltersService from "../../services/filters";
import { selectJobState, selectMappings, selectWizardFilters } from "../../store/selectors";
import type { CrmEnrichmentJobDTO } from "../../types/types";
import { areMappingsUnchanged, areMappingsValid } from "../../utils/mappingValidation";

type HookParams = {
    initialJob?: CrmEnrichmentJobDTO;
    columns: readonly CrmColumnItem[];
};

type HookValue = {
    isNextButtonDisabled: boolean;
};

const useStatefullEditValidation = ({ initialJob, columns }: HookParams): HookValue => {
    const currentJobState = useSelector(selectJobState);
    const wizardFilters = useSelector(selectWizardFilters);
    const mappings = useSelector(selectMappings);

    const isNextButtonDisabled = useMemo(() => {
        if (!initialJob || wizardFilters.isApplyFiltersSelected === null) {
            return true;
        }

        const { name, enrichmentObject, country, refreshDataInterval, leadScoreRefreshDataInterval, filters: initialFilters } = initialJob;

        const isInitialState = [
            name === currentJobState.jobName,
            enrichmentObject === currentJobState.jobObject,
            country === currentJobState.countryId,
            refreshDataInterval === currentJobState.refreshPeriod,
            (leadScoreRefreshDataInterval ?? "weekly") === (currentJobState.leadScoreRefreshDataInterval || "weekly"),
            (initialFilters.length === 0 && !wizardFilters.isApplyFiltersSelected) ||
                crmEnrichmentFiltersService.areFiltersEqualToInitial({
                    filters: wizardFilters.filters,
                    initialFilters,
                    columns,
                }),
            areMappingsUnchanged(mappings, initialJob.mappings),
        ].every(Boolean);

        return (
            isInitialState ||
            !currentJobState.jobName.trim() ||
            !crmEnrichmentFiltersService.areFiltersValid(wizardFilters) ||
            !areMappingsValid(mappings)
        );
    }, [currentJobState, wizardFilters, initialJob, columns, mappings]);

    return {
        isNextButtonDisabled,
    };
};

export default useStatefullEditValidation;
